import React, { useCallback, useEffect, useState } from 'react';

import { Spinner } from '@ast/magma/components/spinner';

import { EventLogItem } from '@app/queryTyping';

import {
  ActionGroup,
  ActionGroupItem,
} from '@app/common/components/ActionGroup';
import { SearchField } from '@app/common/components/SearchField/SearchField';
import { useIntersectionObserver } from '@app/common/utils/hooks/useIntersectionObserver';

import {
  GetEventLogQuery,
  useGetEventLogQuery,
} from '@app/widgets/contacts-and-settings/queries/queryTyping/getUserEventLogs';

import { EventLogDetails } from './EventLogDetails';
import { Directions, EventLogFilter } from './EventLogFilter';
import { UserEventLogItem } from './UserEventLogItem';
import styles from './UserEventLogs.pcss';

const mergeEvents = (prev: GetEventLogQuery, curr: GetEventLogQuery) => ({
  eventLogs: {
    ...curr.eventLogs,
    data: [...prev.eventLogs.data, ...curr.eventLogs.data],
  },
});

const PAGE_SISE = 15;
const FIRST_PAGE = 1;

const useGetEventLogQueryInfinite = () => {
  const [searhText, setSearchText] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<Directions>(Directions.desc);
  const {
    data, loading, fetchMore, error, refetch,
  } = useGetEventLogQuery({
    variables: {
      pageSize: PAGE_SISE,
      pageNumber: FIRST_PAGE,
      sortDirection,
      freeTextFilter: searhText || '',
      startDate: undefined,
      endDate: undefined,
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  if (error) throw error;

  const [scrollRef, { entry }] = useIntersectionObserver({ threshold: 0 });
  const isVisible = entry?.isIntersecting;
  const [currentPage, setCurrentPage] = useState<number>(FIRST_PAGE);
  const [isFetchMoreLoading, setIsFetchMoreLoading] = useState<boolean>(false);

  const isFetchMoreEnabled = isVisible
    && !loading
    && !isFetchMoreLoading
    && data?.eventLogs.isAvailableMoreData;

  const onSearch = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  const onChangeDirection = useCallback(() => {
    setSortDirection((direction: Directions) => (direction === Directions.desc ? Directions.acs : Directions.desc));
  }, []);

  const onFetchMoreHandler = async () => {
    try {
      const nextPageNumber = currentPage + 1;
      setCurrentPage(nextPageNumber);
      setIsFetchMoreLoading(true);

      await fetchMore({
        variables: {
          pageSize: PAGE_SISE,
          pageNumber: nextPageNumber,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return mergeEvents(prev, fetchMoreResult);
        },
      });
    } finally {
      setIsFetchMoreLoading(false);
    }
  };
  useEffect(() => {
    if (isFetchMoreEnabled) {
      onFetchMoreHandler();
    }
  }, [isVisible]);

  useEffect(() => {
    if (searhText !== null) {
      refetch({
        freeTextFilter: searhText,
        pageNumber: FIRST_PAGE,
        pageSize: PAGE_SISE,
        sortDirection,
      });
    }
  }, [searhText]);

  useEffect(() => {
    setCurrentPage(FIRST_PAGE);
  }, [sortDirection, searhText]);

  return {
    isFetchMoreLoading,
    isFirstPageLoading: loading,
    onSearch,
    sortDirection,
    onChangeDirection,
    scrollRef,
    data,
  };
};

export const UserEventLogs: React.FC = () => {
  const [currentEvent, setCurrentEvent] = useState<EventLogItem | null>(null);

  const {
    scrollRef, data, isFetchMoreLoading, isFirstPageLoading, onSearch, sortDirection, onChangeDirection,
  } = useGetEventLogQueryInfinite();

  const onClose = () => {
    setCurrentEvent(null);
  };

  const events = data?.eventLogs.data || [];

  return (
    <div className={styles.root}>
      <SearchField onChange={onSearch} />
      <EventLogFilter direction={sortDirection} onChangeDirection={onChangeDirection} />
      {isFirstPageLoading ? (
        <div className={styles.spinner} data-stable-name="UserEventLogsSpinner">
          <Spinner />
        </div>
      ) : (
        <>
          <ActionGroup>
            {events.map((event) => (
              <ActionGroupItem
                onSelect={() => setCurrentEvent(event)}
                key={event.id}
              >
                <UserEventLogItem event={event} />
              </ActionGroupItem>
            ))}
          </ActionGroup>
          {data?.eventLogs.isAvailableMoreData && (
            <div className={styles.spinner} ref={scrollRef}>
              {isFetchMoreLoading && <Spinner />}
            </div>
          )}
        </>
      )}
      <EventLogDetails event={currentEvent} onClose={onClose} />
    </div>
  );
};
